/**
 * Field rules for tenant master data, shared by the forms (inline errors) and
 * the demo store (which answers 400 like the API will). Empty optional fields
 * are stored as null, never as "".
 */
import { isValidEmail, isValidTenantCode, normalizeEmail, TENANT_NAME_MAX, TENANT_NAME_MIN } from '@fsv/core';
import type { TenantContact } from '../data/types';
import { TENANT_CONTACT_FIELDS } from '../data/types';
import { tx } from '../i18n/extra';
import { isValidSlug } from './slug';

export const CONTACT_FIELD_MAX = 200;
export const TENANT_NOTES_MAX = 2000;

/** Trimmed value, or null for empty. */
export function optional(value: string | null | undefined): string | null {
  const v = value?.trim() ?? '';
  return v === '' ? null : v;
}

export type TenantFieldErrors = Partial<Record<keyof TenantContact | 'name' | 'slug' | 'code' | 'notes' | 'adminName' | 'adminEmail', string>>;

export function validateTenantName(name: string | undefined): string | null {
  const v = name?.trim() ?? '';
  if (v.length < TENANT_NAME_MIN) return tx.tenant.errors.nameRequired;
  if (v.length > TENANT_NAME_MAX) return tx.tenant.errors.tooLong(TENANT_NAME_MAX);
  return null;
}

export function validateSlugInput(slug: string): string | null {
  return isValidSlug(slug) ? null : tx.tenant.errors.slugInvalid;
}

/** The tenant code exactly as the API stores it; the form upper-cases while typing. */
export function validateCodeInput(code: string): string | null {
  return isValidTenantCode(code) ? null : tx.tenant.errors.codeInvalid;
}

/** Contact fields (all optional) plus notes. */
export function validateContact(values: Partial<TenantContact> & { notes?: string | null | undefined }): TenantFieldErrors {
  const errors: TenantFieldErrors = {};
  for (const k of TENANT_CONTACT_FIELDS) {
    const v = optional(values[k]);
    if (v !== null && v.length > CONTACT_FIELD_MAX) errors[k] = tx.tenant.errors.tooLong(CONTACT_FIELD_MAX);
  }
  const email = optional(values.contactEmail);
  if (email !== null && !errors.contactEmail && !isValidEmail(normalizeEmail(email))) errors.contactEmail = tx.tenant.errors.emailInvalid;
  const web = optional(values.website);
  if (web !== null && !errors.website && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(web)) errors.website = tx.tenant.errors.websiteInvalid;
  const notes = optional(values.notes);
  if (notes !== null && notes.length > TENANT_NOTES_MAX) errors.notes = tx.tenant.errors.tooLong(TENANT_NOTES_MAX);
  return errors;
}

export function firstError(errors: TenantFieldErrors): string | null {
  return Object.values(errors).find((e): e is string => typeof e === 'string') ?? null;
}
